/**
 * Footer — iGaming operator standard.
 * 4-column desktop grid, mobile accordions, trust bar, compliance strip.
 *
 * Rendered inside AppShell for all shelled pages. Auth pages (/login, /signup,
 * /verify-email, /blocked) and the immersive /game route render outside of
 * AppShell and therefore do not surface this footer.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Twitter, Instagram, Youtube, Send, MessageCircle,
  ChevronDown, Bitcoin, ShieldCheck, Lock,
} from "lucide-react";

const GAMES_LINKS = [
  { label: "Bra 3-Way", to: "/game/bra-3-way", live: true },
  { label: "Bra 2-Way", to: "/game/bra-2-way", live: true },
  { label: "Sexy Tattoo", to: "/game/sexy-tattoo", live: true },
  { label: "Hot Bikini", to: "/game/hot-bikini", live: true },
  { label: "Scratch Card", to: "/game/scratch-card", live: true },
  { label: "Slots", soon: true },
  { label: "Live Casino", soon: true },
  { label: "Table Games", soon: true },
  { label: "Crash Games", soon: true },
  { label: "View All Games", to: "/", arrow: true },
];

const SUPPORT_LINKS = [
  { label: "Help Center", chat: true },
  { label: "Live Chat", chat: true },
  { label: "Contact Us", href: "mailto:support@bragame.com" },
  { label: "FAQ", chat: true },
  { label: "Get the App", to: "/download" },
  { label: "Responsible Gambling", to: "/settings/responsible-gambling" },
  { label: "Anti Money Laundering Policy", href: "#aml" },
  { label: "Fairness & RNG", to: "/verify" },
];

const ABOUT_LINKS = [
  { label: "About Bra Game", href: "#about" },
  { label: "Terms of Service", href: "#tos" },
  { label: "Privacy Policy", href: "#privacy" },
  { label: "Cookie Policy", href: "#cookies" },
  { label: "Bonus Terms", href: "#bonus" },
  { label: "Affiliate Program", soon: true },
  { label: "Careers", soon: true },
];

const SOCIALS = [
  { key: "twitter", icon: Twitter, label: "X (Twitter)", href: "#" },
  { key: "instagram", icon: Instagram, label: "Instagram", href: "#" },
  { key: "telegram", icon: Send, label: "Telegram", href: "#" },
  { key: "discord", icon: MessageCircle, label: "Discord", href: "#" },
  { key: "youtube", icon: Youtube, label: "YouTube", href: "#" },
];

const CARD_PAYMENTS = ["VISA", "Mastercard", "AmEx", "Skrill", "Neteller"];
const CRYPTO_PAYMENTS = ["Ethereum", "USDT", "Litecoin", "USDC"];

const PROVIDERS = ["PragmaticBra", "EvoBra", "SoftBra", "MicroBra", "BraGaming", "PlayBra"];

const RG_BADGES = [
  { label: "GamCare", href: "https://www.gamcare.org.uk" },
  { label: "BeGambleAware", href: "https://www.begambleaware.org" },
  { label: "GamStop", href: "https://www.gamstop.co.uk" },
  { label: "IBAS Dispute Resolution", href: "https://ibas-uk.com" },
];

const openChat = () => window.__openBraGameChat?.();

export default function Footer() {
  return (
    <footer
      className="mt-auto bg-elevated/40 border-t border-line"
      data-testid="app-footer"
    >
      {/* 4-column grid (desktop) / accordions (mobile) */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-10 pb-6">
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-8">
          <FooterColumnDesktop title="Games" testid="footer-col-games">
            {GAMES_LINKS.map((l) => <FooterLink key={l.label} {...l} />)}
          </FooterColumnDesktop>
          <FooterColumnDesktop title="Support" testid="footer-col-support">
            {SUPPORT_LINKS.map((l) => <FooterLink key={l.label} {...l} />)}
          </FooterColumnDesktop>
          <FooterColumnDesktop title="About" testid="footer-col-about">
            {ABOUT_LINKS.map((l) => <FooterLink key={l.label} {...l} />)}
          </FooterColumnDesktop>
          <FooterColumnDesktop title="Stay connected" testid="footer-col-connect">
            <StayConnected />
          </FooterColumnDesktop>
        </div>

        <div className="md:hidden divide-y divide-line">
          <FooterAccordion title="Games" testid="footer-col-games">
            {GAMES_LINKS.map((l) => <FooterLink key={l.label} {...l} />)}
          </FooterAccordion>
          <FooterAccordion title="Support" testid="footer-col-support">
            {SUPPORT_LINKS.map((l) => <FooterLink key={l.label} {...l} />)}
          </FooterAccordion>
          <FooterAccordion title="About" testid="footer-col-about">
            {ABOUT_LINKS.map((l) => <FooterLink key={l.label} {...l} />)}
          </FooterAccordion>
          <FooterAccordion title="Stay connected" testid="footer-col-connect">
            <StayConnected />
          </FooterAccordion>
        </div>
      </div>

      {/* Trust bar */}
      <div className="border-t border-line bg-base" data-testid="footer-trust-bar">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-6 space-y-5">
          <TrustRow label="Payment methods">
            <div className="flex flex-wrap gap-2" data-testid="footer-payments">
              {CARD_PAYMENTS.map((p) => <TrustBadge key={p} label={p} />)}
              <TrustBadge label="Bitcoin" icon={Bitcoin} />
              {CRYPTO_PAYMENTS.map((c) => <TrustBadge key={c} label={c} />)}
            </div>
          </TrustRow>

          <TrustRow label="Game providers">
            <div className="flex flex-wrap gap-2" data-testid="footer-providers">
              {PROVIDERS.map((p) => (
                <span
                  key={p}
                  className="px-4 py-2 rounded-md border border-line bg-elevated text-ink-3 hover:text-ink hover:border-ink-2 transition font-display italic text-sm opacity-70 hover:opacity-100"
                >
                  {p}
                </span>
              ))}
            </div>
          </TrustRow>

          <TrustRow label="Compliance & fairness">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3" data-testid="footer-license">
              <div className="flex items-center gap-2 px-4 py-2 rounded-md border border-gold/40 bg-gold/5">
                <ShieldCheck className="w-4 h-4 text-gold" />
                <div className="leading-tight">
                  <div className="text-[9px] tracking-[0.3em] uppercase text-gold font-semibold">Licensed by AGA</div>
                  <div className="text-[11px] font-mono text-ink-2">
                    License #ALSI-142857-01 ·{" "}
                    <a href="#verify-license" className="text-pink hover:underline" data-testid="footer-license-verify">
                      Verify
                    </a>
                  </div>
                </div>
              </div>
              <span
                className="w-9 h-9 rounded-full border border-gold/40 text-[10px] font-bold flex items-center justify-center text-gold"
                title="18+ only"
                data-testid="footer-18plus"
              >
                18+
              </span>
              {RG_BADGES.map((b) => (
                <a
                  key={b.label}
                  href={b.href}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-2 rounded-md border border-line bg-elevated text-ink-3 hover:text-ink hover:border-ink-2 transition text-[11px] tracking-wide font-semibold"
                  data-testid={`footer-rg-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {b.label}
                </a>
              ))}
              <div
                className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-line bg-elevated text-ink-2 text-[11px]"
                data-testid="footer-ssl"
              >
                <Lock className="w-3 h-3 text-green" />
                <span>Secured by SSL · Cloudflare Protected</span>
              </div>
            </div>
          </TrustRow>
        </div>
      </div>

      {/* Compliance strip */}
      <div className="border-t border-line bg-base" data-testid="footer-compliance">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-6 space-y-3">
          <p className="text-[11px] leading-relaxed text-ink-3 max-w-5xl">
            Bra Game N.V. is registered in Anjouan, Comoros, under license
            ALSI-142857-01 issued by the Anjouan Gaming Authority.
            Gambling can be addictive — please play responsibly. If you or
            someone you know has a gambling problem and wants help, call the
            National Council on Problem Gambling helpline:{" "}
            <a href="tel:18005224700" className="text-ink-2 hover:text-ink">
              1-800-522-4700
            </a>
            . This service is not available in restricted jurisdictions. You
            must be 18+ to play.
          </p>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 pt-3 border-t border-line">
            <div className="text-[11px] text-ink-3">
              © 2026 Bra Game N.V. All rights reserved.
            </div>
            <div className="flex items-center gap-4 text-[10px] tracking-widest uppercase text-ink-3">
              <Link to="/settings/responsible-gambling" className="hover:text-ink">Play responsibly</Link>
              <Link to="/verify" className="hover:text-ink">Provably fair</Link>
              <a href="#sitemap" className="hover:text-ink">Sitemap</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumnDesktop({ title, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-3 font-semibold">
        {title}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function FooterAccordion({ title, testid, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testid}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-3 text-left"
        data-testid={`${testid}-toggle`}
      >
        <span className="text-[10px] tracking-[0.3em] uppercase text-gold font-semibold">
          {title}
        </span>
        <ChevronDown className={"w-4 h-4 text-ink-3 transition-transform " + (open ? "rotate-180" : "")} />
      </button>
      {open && <div className="pb-3 space-y-2">{children}</div>}
    </div>
  );
}

function FooterLink({ label, to, href, chat, soon, arrow, live }) {
  const cls =
    "flex items-center gap-2 text-[13px] transition " +
    (soon ? "text-ink-3 cursor-not-allowed" : "text-ink-2 hover:text-ink");
  const testid =
    "footer-link-" + label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const inner = (
    <>
      <span>{label}</span>
      {live && (
        <span className="inline-flex items-center gap-1 text-[8px] tracking-widest uppercase text-green font-semibold">
          <span className="w-1 h-1 rounded-full bg-green animate-pulse" />
          Live
        </span>
      )}
      {soon && (
        <span className="text-[8px] tracking-widest uppercase text-ink-3 border border-line rounded px-1.5 py-0.5">
          Soon
        </span>
      )}
      {arrow && <span>→</span>}
    </>
  );
  if (soon)
    return (
      <span className={cls} data-testid={testid} title="Coming soon">
        {inner}
      </span>
    );
  if (chat)
    return (
      <button onClick={openChat} className={cls + " text-left"} data-testid={testid}>
        {inner}
      </button>
    );
  if (to)
    return (
      <Link to={to} className={cls} data-testid={testid}>
        {inner}
      </Link>
    );
  return (
    <a href={href || "#"} className={cls} data-testid={testid}>
      {inner}
    </a>
  );
}

function StayConnected() {
  const [email, setEmail] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    toast.success("Subscribed! You'll hear from us soon.");
    setEmail("");
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap" data-testid="footer-socials">
        {SOCIALS.map((s) => (
          <a
            key={s.key}
            href={s.href}
            className="w-9 h-9 rounded-md border border-line bg-elevated hover:border-pink hover:text-pink text-ink-2 flex items-center justify-center transition"
            title={s.label}
            data-testid={`footer-social-${s.key}`}
          >
            <s.icon className="w-4 h-4" />
          </a>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-2" data-testid="footer-newsletter">
        <div className="text-[10px] tracking-widest uppercase text-ink-3">
          Get exclusive offers
        </div>
        <div className="flex items-stretch gap-1">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className="flex-1 min-w-0 h-10 rounded-md bg-elevated border border-line px-3 text-sm text-ink placeholder-ink-3 focus:outline-none focus:border-pink transition"
            data-testid="footer-newsletter-input"
          />
          <button
            type="submit"
            className="h-10 px-3 rounded-md bg-brand-gradient text-white text-xs font-semibold uppercase tracking-widest shadow-glow-pink hover:brightness-110 transition"
            data-testid="footer-newsletter-submit"
          >
            Subscribe
          </button>
        </div>
      </form>
    </div>
  );
}

function TrustRow({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
      <div className="text-[9px] tracking-[0.3em] uppercase text-ink-3 sm:w-40 shrink-0">
        {label}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function TrustBadge({ label, icon: Icon }) {
  const testid =
    "footer-payment-" + label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-line bg-elevated text-ink-3 hover:text-ink hover:border-ink-2 transition text-[11px] font-mono uppercase tracking-widest"
      data-testid={testid}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </span>
  );
}
